/**
 * Newsletter popup (snippets/tcs-newsletter-popup.liquid,
 * tasks/features/2026-09-28-newsletter-popup.md).
 *
 * The popup is a native modal <dialog> around Shopify's own customer form. This
 * script never submits, validates or rewrites that form: Shopify owns the POST,
 * the subscription, the tags and the success/error response, exactly as for the
 * footer. It only decides WHEN the dialog opens and manages focus.
 *
 * - Owner decision (Daria, 2026-09-28): "popup should open in 10 sec, no matter
 *   number of pages. no reset." Any page view is eligible, the first included.
 * - Runs only in a document the shopper actually sees. A prerendered document
 *   (`document.prerendering`, e.g. Chrome speculating on a collection link) does
 *   nothing — not even the storage probe — until `prerenderingchange`; a document
 *   hidden at load waits for its first `visibilitychange` to visible. Until then
 *   nothing is read from or written to storage.
 * - One countdown per page load, of data-delay-seconds, from that moment. It is
 *   never restarted: when it has elapsed and something below blocks the popup,
 *   it re-checks every RETRY_MS and opens as soon as the block clears. It opens
 *   only while: the tab is visible (a visibilitychange listener, not polling); no other
 *   dialog, Horizon scroll lock or consent/app modal is showing; no consent
 *   decision is pending (Shopify's Customer Privacy API: the region needs a
 *   banner — `shouldShowBanner()`, which stays true after a choice — AND
 *   `currentVisitorConsent()` has no recorded marketing/analytics/preferences
 *   value; "API not loaded yet" waits up to CONSENT_API_WAIT_MS, and an API
 *   that throws counts as not pending — either way the Consentmo markup check,
 *   always on as a second signal, then decides); focus is not in a text field or an iframe (followed through open
 *   shadow roots), nor in the header navigation / an open mega menu; and no
 *   keydown or pointerdown happened in the last INPUT_QUIET_MS. Re-checked
 *   every second.
 * - Suppression has three levels, each written by a different event:
 *   - impression: opening writes sessionStorage `shown`, so it does not return
 *     in this browser session, however it was left;
 *   - explicit dismissal: the close button, "No thanks", Escape or a backdrop
 *     click writes localStorage `dismissed`, suppressing it for
 *     data-dismiss-days (the Theme Editor setting) — also when the popup had
 *     reopened on a Shopify error. Closing it after a success is not;
 *   - signup: a success writes localStorage `subscribed`, which never expires —
 *     only when this browser submitted (the popup's own marker, or the footer
 *     marker written on a valid footer submit). A bare `?customer_posted=true`
 *     URL renders Shopify's success state too, and must not suppress forever.
 *     A Shopify error itself writes nothing: left open and navigated away from,
 *     only the session impression applies.
 *   Without localStorage a dismissal lasts the session; without
 *   sessionStorage it never auto-opens (no way to stop it repeating).
 * - Storage is strictly necessary (docs/rulings.md R-043): the dismissal record
 *   is the shopper's own choice, the impression is a session-scoped frequency
 *   cap with no identifier, the probe leaves nothing
 *   behind, and nothing is shown (so no impression written) before a consent
 *   decision.
 * - A valid submit writes a short-lived sessionStorage marker. After Shopify's
 *   reload the popup reopens only when that marker exists, because Shopify's
 *   `form` state is shared with the footer's customer form. On that reopen the
 *   footer's copy of the same result is hidden (observed on the PR #354 preview,
 *   2026-09-28); a footer submission keeps its own message untouched.
 *
 * COUPLING NOTE: lockScroll/unlockScroll from assets/utilities.js (Horizon 4.1.3)
 * — the same owner-set scroll lock the cart drawer and dialog-component use.
 * COUPLING NOTE: Consentmo's cookie dialog — a <csm-cookie-consent> host
 * (class csm-cmp-root) whose open shadow root holds div[role="dialog"]
 * [aria-modal="true"] "Cookie dialog" (recorded on Live, 2026-09-18:
 * docs/accessibility/evidence/2026-09-18/lane-c/). While it is visible the
 * popup waits; it never touches Consentmo. If Consentmo is absent, its shadow
 * is closed or its markup changes, the check finds nothing and the popup keeps
 * its normal behaviour (fails open, never stuck closed).
 * COUPLING NOTE: the footer result ids in sections/tcs-footer.liquid
 * (TcsFooterNewsletterSuccess-*, TcsFooterNewsletterError-*, TcsFooterEmail-*).
 * Tripwire for both: scripts/tests/tcs-newsletter-popup-static.test.mjs.
 */
import { lockScroll, unlockScroll } from '@theme/utilities';

const STATE_KEY = 'tcs:newsletter-popup';
const SESSION_KEY = 'tcs:newsletter-popup-shown';
const POSTED_KEY = 'tcs:newsletter-popup-posted';
const POSTED_MAX_AGE = 10 * 60_000;
const DAY = 86_400_000;
const RETRY_MS = 1000;
// A valid footer submit: lets its success record `subscribed` (a bare result URL cannot).
const FOOTER_POSTED_KEY = 'tcs:newsletter-popup-footer-posted';
// Accessibility: never open straight after the shopper pressed a key or the pointer.
const INPUT_QUIET_MS = 2000;
// How long "Customer Privacy API not loaded yet" counts as "no consent decision yet".
const CONSENT_API_WAIT_MS = 10_000;
// A non-empty value for any of these in currentVisitorConsent() means a choice was recorded.
const CONSENT_PURPOSES = ['marketing', 'analytics', 'preferences'];
const HEADER_NAV = 'header-menu';
const OPEN_MEGA_MENU = 'header-menu [aria-expanded="true"]';
const FOOTER_RESULT = '[id^="TcsFooterNewsletterSuccess-"], [id^="TcsFooterNewsletterError-"]';
// Consent / third-party modals the popup must never cover.
const CONSENT_HOST = 'csm-cookie-consent, .csm-cmp-root';
const MODAL_LAYER = '[aria-modal="true"]';
const TEXT_ENTRY =
  'input:not([type="checkbox"], [type="radio"], [type="button"], [type="submit"]), textarea, select, [contenteditable="true"]';

/**
 * Whether Shopify's Customer Privacy API says a consent decision is still pending:
 * the region requires a banner AND no choice is recorded. `shouldShowBanner()` alone is
 * not enough — it is region-level and stays true after Accept or Reject (verified on a
 * test theme with Consentmo, 2026-09-28); Consentmo syncs the choice to
 * `currentVisitorConsent()` as "yes"/"no", which is empty ("") before any choice.
 * An API that throws or lacks either method counts as not pending, so the Consentmo
 * markup check alone decides (never blocked forever by a broken API).
 * @param {any} privacy - window.Shopify.customerPrivacy
 * @returns {boolean}
 */
const consentDecisionPending = (privacy) => {
  try {
    if (privacy.shouldShowBanner() !== true) return false;
    const consent = privacy.currentVisitorConsent();
    return !CONSENT_PURPOSES.some(
      (purpose) => typeof consent?.[purpose] === 'string' && consent[purpose] !== '',
    );
  } catch {
    return false;
  }
};

/** @param {'localStorage' | 'sessionStorage'} type */
const usableStorage = (type) => {
  try {
    const store = window[type];
    store.setItem(`${STATE_KEY}:probe`, '1');
    store.removeItem(`${STATE_KEY}:probe`);
    return store;
  } catch {
    return null;
  }
};

/** The focused element, followed through open shadow roots. */
const deepActiveElement = () => {
  let active = document.activeElement;
  while (active?.shadowRoot?.activeElement) active = active.shadowRoot.activeElement;
  return active;
};

/**
 * closest() across shadow boundaries.
 * @param {Element} element
 * @param {string} selector
 */
const composedClosest = (element, selector) => {
  /** @type {Node | null} */
  let node = element;
  while (node) {
    if (node instanceof Element && node.matches(selector)) return node;
    node = node.parentNode instanceof ShadowRoot ? node.parentNode.host : node.parentNode;
  }
  return null;
};

/** @param {Element} element */
const isShown = (element) => {
  const box = element.getBoundingClientRect();
  if (box.width < 1 || box.height < 1) return false;
  if (typeof element.checkVisibility === 'function') {
    return element.checkVisibility({ checkOpacity: true, checkVisibilityCSS: true });
  }
  const style = getComputedStyle(element);
  return style.visibility !== 'hidden' && style.display !== 'none' && Number(style.opacity) > 0;
};

class TcsNewsletterPopup extends HTMLElement {
  /** @type {HTMLDialogElement | null} */
  #dialog = null;
  /** @type {HTMLElement | null} */
  #returnFocus = null;
  #restoreFocus = true;
  /** @type {number | undefined} */
  #timer;
  /** @type {Storage | null} Probed in #activateWhenShown, never in an unseen document. */
  #local = null;
  /** @type {Storage | null} */
  #session = null;
  #activated = false;
  /** @type {'error' | 'success' | null} Shopify result the open popup is showing. */
  #showing = null;
  #dismissed = false;
  /** The current pointer press began on the backdrop (see #onBackdrop). */
  #pressOnBackdrop = false;
  #startedAt = Date.now();
  #lastInput = -Infinity;
  #waitingForVisible = false;
  #waitingToActivate = false;

  connectedCallback() {
    const dialog = this.querySelector('dialog');
    if (!(dialog instanceof HTMLDialogElement) || this.#dialog) return;
    this.#dialog = dialog;

    dialog.addEventListener('close', this.#handleClose);
    dialog.addEventListener('pointerdown', (event) => {
      this.#pressOnBackdrop = this.#onBackdrop(event);
    });
    dialog.addEventListener('click', this.#handleBackdropClick);
    // Escape: the dialog's cancel event (the close watcher may skip it, then only close fires).
    dialog.addEventListener('cancel', () => (this.#dismissed = true));
    dialog.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') this.#dismissed = true;
    });
    this.querySelector('form')?.addEventListener('submit', this.#handleSubmit);
    for (const button of this.querySelectorAll('[data-tcs-newsletter-popup-close]')) {
      button.addEventListener('click', () => this.dismiss());
    }
    document.addEventListener('keydown', this.#noteInput, { capture: true, passive: true });
    document.addEventListener('pointerdown', this.#noteInput, { capture: true, passive: true });
    document.addEventListener('submit', this.#handleFooterSubmit, { capture: true });

    this.#activateWhenShown();
  }

  disconnectedCallback() {
    window.clearTimeout(this.#timer);
    document.removeEventListener('visibilitychange', this.#openWhenClear);
    document.removeEventListener('prerenderingchange', this.#activateWhenShown);
    document.removeEventListener('visibilitychange', this.#activateWhenShown);
    document.removeEventListener('keydown', this.#noteInput, { capture: true });
    document.removeEventListener('pointerdown', this.#noteInput, { capture: true });
    document.removeEventListener('submit', this.#handleFooterSubmit, { capture: true });
    if (this.#dialog) unlockScroll(this.#dialog);
  }

  /**
   * Starts the popup once the document is really shown: after a prerendered document is
   * activated, and once a document loaded in a background tab first becomes visible.
   * Chrome prerenders same-origin pages the shopper has not opened; those must not probe
   * storage, count down or write the session impression.
   */
  #activateWhenShown = () => {
    if (this.#activated || !this.isConnected) return;
    const hidden =
      /** @type {any} */ (document).prerendering === true || document.visibilityState !== 'visible';
    if (hidden) {
      if (!this.#waitingToActivate) {
        this.#waitingToActivate = true;
        document.addEventListener('prerenderingchange', this.#activateWhenShown);
        document.addEventListener('visibilitychange', this.#activateWhenShown);
      }
      return;
    }
    if (this.#waitingToActivate) {
      this.#waitingToActivate = false;
      document.removeEventListener('prerenderingchange', this.#activateWhenShown);
      document.removeEventListener('visibilitychange', this.#activateWhenShown);
    }
    this.#activated = true;
    this.#startedAt = Date.now();
    this.#local = usableStorage('localStorage');
    this.#session = usableStorage('sessionStorage');
    this.#start();
  };

  #start() {
    const posted = this.#takeMarker(POSTED_KEY);
    const footerPosted = this.#takeMarker(FOOTER_POSTED_KEY);
    const result = this.querySelector('[data-tcs-newsletter-popup-result]');
    const succeeded =
      result instanceof HTMLElement && result.dataset.tcsNewsletterPopupResult === 'success';

    // Only a submission made in this browser is a signup; a shared `?customer_posted=true`
    // link renders the same success state and must not suppress the offer forever.
    if (succeeded && (posted || footerPosted)) this.#remember('subscribed');

    if (posted && result instanceof HTMLElement) {
      const form = this.querySelector('form');
      if (form?.id && window.location.hash === `#${form.id}`) {
        history.replaceState(
          history.state,
          '',
          `${window.location.pathname}${window.location.search}`,
        );
      }
      this.#hideFooterEcho();
      const field = this.querySelector('input[type="email"]');
      // No shopper opener exists after a reload; whatever took focus on load (the footer
      // field's own error autofocus) must not receive it back on close.
      this.open(succeeded || !(field instanceof HTMLElement) ? result : field, false);
      this.#showing = succeeded ? 'success' : 'error';
      this.#write(this.#session, SESSION_KEY, '1');
      return;
    }

    // A footer result (Shopify renders it in both forms): never interrupt it.
    if (result) return;
    if (this.#suppressed()) return;

    // The one countdown of this page load, from the moment it was shown. Retries below
    // re-check every RETRY_MS and never schedule the delay again.
    const delay = Math.max(0, Number(this.dataset.delaySeconds) || 0) * 1000;
    this.#timer = window.setTimeout(this.#openWhenClear, delay);
  }

  /**
   * Shopify renders one customer-form result into every customer form on the page, so
   * the footer repeats what the popup reports. Only the popup's own submission gets
   * here; the footer copy is hidden and its field no longer points at it (a hidden
   * aria-describedby target is still read).
   */
  #hideFooterEcho() {
    for (const message of document.querySelectorAll(FOOTER_RESULT)) {
      if (!(message instanceof HTMLElement) || this.contains(message)) continue;
      message.hidden = true;
      for (const field of document.querySelectorAll(`[aria-describedby="${message.id}"]`)) {
        field.removeAttribute('aria-describedby');
        field.removeAttribute('aria-invalid');
      }
    }
  }

  #openWhenClear = () => {
    if (document.visibilityState !== 'visible') {
      // Hidden tab: wait for it to come back instead of polling.
      if (!this.#waitingForVisible) {
        this.#waitingForVisible = true;
        document.addEventListener('visibilitychange', this.#openWhenClear);
      }
      return;
    }
    if (this.#waitingForVisible) {
      this.#waitingForVisible = false;
      document.removeEventListener('visibilitychange', this.#openWhenClear);
    }
    if (this.#blocked()) {
      // The delay has elapsed: poll for the block to clear, never restart the countdown.
      this.#timer = window.setTimeout(this.#openWhenClear, RETRY_MS);
      return;
    }
    if (this.#suppressed()) return;
    this.#write(this.#session, SESSION_KEY, '1');
    this.open();
  };

  #blocked() {
    if (document.documentElement.hasAttribute('scroll-lock')) return true;
    if (document.querySelector('dialog:modal')) return true;
    if (this.#consentPending()) return true;
    if (this.#foreignModalVisible()) return true;
    if (Date.now() - this.#lastInput < INPUT_QUIET_MS) return true;
    if (document.querySelector(OPEN_MEGA_MENU)) return true;
    const active = deepActiveElement();
    if (!(active instanceof Element) || active === document.body) return false;
    // A focused iframe (chat, payment, CAPTCHA widget) may be taking input we cannot see.
    if (active.tagName === 'IFRAME') return true;
    if (composedClosest(active, HEADER_NAV)) return true;
    return (
      (active instanceof HTMLElement && active.isContentEditable) || active.matches(TEXT_ENTRY)
    );
  }

  #noteInput = () => {
    this.#lastInput = Date.now();
  };

  /**
   * A consent decision is pending (consentDecisionPending). Until the API has loaded
   * that also counts as undecided, for at most CONSENT_API_WAIT_MS; after that the
   * Consentmo markup check alone decides.
   */
  #consentPending() {
    try {
      const privacy = /** @type {any} */ (window).Shopify?.customerPrivacy;
      if (typeof privacy?.shouldShowBanner === 'function') return consentDecisionPending(privacy);
      return Date.now() - this.#startedAt < CONSENT_API_WAIT_MS;
    } catch {
      return false;
    }
  }

  /**
   * A visible aria-modal layer that is not this popup: the Consentmo cookie dialog
   * (inside its open shadow root) or any other app's modal in the page. The
   * "Cookie Preferences" footer link and small floating widgets are not modal,
   * so they never hold the popup back.
   */
  #foreignModalVisible() {
    try {
      const candidates = [...document.querySelectorAll(MODAL_LAYER)];
      for (const host of document.querySelectorAll(CONSENT_HOST)) {
        if (host.shadowRoot) candidates.push(...host.shadowRoot.querySelectorAll(MODAL_LAYER));
      }
      return candidates.some((element) => !this.contains(element) && isShown(element));
    } catch {
      return false;
    }
  }

  /**
   * @param {HTMLElement} [focusTarget]
   * @param {boolean} [restoreFocus] - return focus on close to the element that had it
   */
  open(focusTarget, restoreFocus = true) {
    const dialog = this.#dialog;
    if (!dialog || dialog.open) return;

    const active = document.activeElement;
    this.#showing = null;
    this.#dismissed = false;
    this.#restoreFocus = restoreFocus;
    this.#returnFocus = active instanceof HTMLElement && active !== document.body ? active : null;

    lockScroll(dialog);
    dialog.showModal();
    (focusTarget ?? dialog).focus();
  }

  close() {
    this.#dialog?.close();
  }

  /** An explicit "not now": close button, "No thanks", Escape or backdrop. */
  dismiss() {
    this.#dismissed = true;
    this.close();
  }

  #handleClose = () => {
    if (this.#dialog) unlockScroll(this.#dialog);
    // Owner ruling 2026-09-28: an explicit close is a dismissal even on the error view.
    // After a success the shopper is already `subscribed`, which #remember never downgrades.
    if (this.#dismissed && this.#showing !== 'success') this.#remember('dismissed');
    this.#dismissed = false;
    const target = this.#returnFocus;
    this.#returnFocus = null;
    if (!this.#restoreFocus) {
      // The browser's own dialog focus restore may already have moved focus back.
      if (target && document.activeElement === target) target.blur();
      return;
    }
    if (target?.isConnected) target.focus({ preventScroll: true });
  };

  /**
   * An event on the ::backdrop: its target is the dialog itself and it lies outside the
   * card's box (a press on the dialog's own scrollbar is inside it).
   * @param {MouseEvent} event
   */
  #onBackdrop(event) {
    const dialog = this.#dialog;
    if (!dialog || event.target !== dialog) return false;
    const box = dialog.getBoundingClientRect();
    return (
      event.clientX < box.left ||
      event.clientX > box.right ||
      event.clientY < box.top ||
      event.clientY > box.bottom
    );
  }

  /**
   * Dismiss only when the press AND the click were both on the backdrop: selecting text or
   * pressing in the email field and releasing outside the card is not "not now".
   */
  #handleBackdropClick = (/** @type {MouseEvent} */ event) => {
    const pressed = this.#pressOnBackdrop;
    this.#pressOnBackdrop = false;
    if (pressed && this.#onBackdrop(event)) this.dismiss();
  };

  #handleSubmit = (/** @type {SubmitEvent} */ event) => {
    const form = event.currentTarget;
    if (!(form instanceof HTMLFormElement) || !form.checkValidity()) return;
    // Without the marker Shopify's result still renders (in the footer too); the popup just stays closed.
    this.#writeMarker(POSTED_KEY);
  };

  /** A valid submit of another customer (newsletter) form on the page, i.e. the footer's. */
  #handleFooterSubmit = (/** @type {SubmitEvent} */ event) => {
    // Structural, not circumstantial: a document never shown writes no storage (R-043).
    if (!this.#activated) return;
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || this.contains(form)) return;
    const type = form.elements.namedItem('form_type');
    if (!(type instanceof HTMLInputElement) || type.value !== 'customer') return;
    if (form.checkValidity()) this.#writeMarker(FOOTER_POSTED_KEY);
  };

  /** @param {string} key */
  #writeMarker(key) {
    try {
      sessionStorage.setItem(
        key,
        JSON.stringify({ path: window.location.pathname, savedAt: Date.now() }),
      );
    } catch {
      // No storage: the result still renders; nothing is remembered.
    }
  }

  /**
   * Reads and always removes a submit marker; true only for this path within POSTED_MAX_AGE.
   * @param {string} key
   */
  #takeMarker(key) {
    try {
      const raw = sessionStorage.getItem(key);
      sessionStorage.removeItem(key);
      if (!raw) return false;
      const value = JSON.parse(raw);
      return (
        value.path === window.location.pathname && Date.now() - value.savedAt <= POSTED_MAX_AGE
      );
    } catch {
      return false;
    }
  }

  #suppressed() {
    if (!this.#session) return true;
    try {
      if (this.#session.getItem(SESSION_KEY)) return true;
      const value = JSON.parse(this.#local?.getItem(STATE_KEY) ?? 'null');
      if (value?.state === 'subscribed') return true;
      if (value?.state !== 'dismissed' || typeof value.at !== 'number') return false;
      return Date.now() - value.at < (Number(this.dataset.dismissDays) || 0) * DAY;
    } catch {
      return true;
    }
  }

  /** @param {'dismissed' | 'subscribed'} state */
  #remember(state) {
    try {
      const current = JSON.parse(this.#local?.getItem(STATE_KEY) ?? 'null');
      if (current?.state === 'subscribed') return;
    } catch {
      // An unreadable record is replaced.
    }
    this.#write(this.#local, STATE_KEY, JSON.stringify({ state, at: Date.now() }));
  }

  /**
   * @param {Storage | null} store
   * @param {string} key
   * @param {string} value
   */
  #write(store, key, value) {
    try {
      store?.setItem(key, value);
    } catch {
      // Storage became unavailable after the probe; suppression falls back to what was written.
    }
  }
}

if (!customElements.get('tcs-newsletter-popup')) {
  customElements.define('tcs-newsletter-popup', TcsNewsletterPopup);
}
